import isNetworkError from 'is-network-error'
import { isTest } from '../is-env'

export interface FailedAttemptContext {
  readonly error: Error
  readonly attemptNumber: number
  readonly retriesLeft: number
}

export interface RetryOptions {
  /** Max retries (not counting initial attempt). Default: 10. Infinity for unlimited. */
  retries?: number
  /** Backoff factor. Default: 1 (constant delay); values <= 0 are treated as 1 */
  factor?: number
  /** Initial delay ms. Default: 1000 */
  minTimeout?: number
  /** Max delay cap ms. Default: Infinity */
  maxTimeout?: number
  /** Add jitter via (Math.random()+1) multiplier. Default: false */
  randomize?: boolean
  /** Abort signal for cancellation */
  signal?: AbortSignal
  /** Called after each failed attempt */
  onFailedAttempt?: (ctx: FailedAttemptContext) => void | Promise<void>
  /** Return false to stop retrying. Default: true (except non-network TypeErrors) */
  shouldRetry?: (ctx: FailedAttemptContext) => boolean | Promise<boolean>
  /** Override delay computation. Receives the default-computed delay ms. */
  computeDelay?: (
    ctx: FailedAttemptContext & { computedDelayMs: number },
  ) => number
}

function calculateDelay(
  retriesConsumed: number,
  opts: Required<
    Pick<RetryOptions, 'factor' | 'minTimeout' | 'maxTimeout' | 'randomize'>
  >,
) {
  const attempt = Math.max(1, retriesConsumed + 1)
  const random = opts.randomize ? Math.random() + 1 : 1
  // 0 * factor^n is 0 for every n; skip the product so float overflow (factor^n = Infinity) can't turn it into NaN
  const timeout =
    opts.minTimeout === 0
      ? 0
      : Math.round(random * opts.minTimeout * opts.factor ** (attempt - 1))
  return Math.min(timeout, opts.maxTimeout)
}

// Always a timer, even for 0 ms (as node-retry's RetryOperation#retry): a 0-delay retry that resolved as a
// microtask would let `retries: Infinity` against a synchronously failing fn starve the event loop forever.
function sleep(ms: number, signal?: AbortSignal): Promise<void> {
  return new Promise<void>((resolve, reject) => {
    const onAbort = () => {
      clearTimeout(timer)
      // oxlint-disable-next-line typescript/prefer-promise-reject-errors -- signal.reason may not be Error; preserve user-provided reason
      reject(signal?.reason)
    }
    const timer = setTimeout(() => {
      signal?.removeEventListener('abort', onAbort)
      resolve()
    }, Math.max(0, ms))
    signal?.addEventListener('abort', onAbort, { once: true })
  })
}

export async function retry<T>(
  fn: (attemptNumber: number) => T | Promise<T>,
  options: RetryOptions = {},
): Promise<T> {
  const retries = options.retries ?? 10
  const factor =
    options.factor !== undefined && options.factor > 0 ? options.factor : 1
  const minTimeout = options.minTimeout ?? (isTest ? 0 : 1000)
  const maxTimeout = options.maxTimeout ?? Number.POSITIVE_INFINITY
  const randomize = options.randomize ?? false
  const onFailedAttempt = options.onFailedAttempt ?? (() => {})
  const shouldRetry = options.shouldRetry ?? (() => true)
  const computeDelay = options.computeDelay
  const { signal } = options

  signal?.throwIfAborted()

  let attemptNumber = 0
  let retriesConsumed = 0

  while (Number.isFinite(retries) ? retriesConsumed <= retries : true) {
    attemptNumber++

    try {
      signal?.throwIfAborted()
      const result = await fn(attemptNumber)
      signal?.throwIfAborted()
      return result
    } catch (error) {
      const normalizedError =
        error instanceof Error
          ? error
          : new TypeError(
              `Non-error was thrown: "${error}". You should only throw errors.`,
            )

      // Non-network TypeErrors are programming bugs — don't retry
      if (
        normalizedError instanceof TypeError &&
        !isNetworkError(normalizedError)
      ) {
        // oxlint-disable-next-line typescript/only-throw-error -- normalizedError is Error by construction above
        throw normalizedError
      }

      const retriesLeft = Number.isFinite(retries)
        ? Math.max(0, retries - retriesConsumed)
        : retries

      const ctx: FailedAttemptContext = Object.freeze({
        error: normalizedError,
        attemptNumber,
        retriesLeft,
      })

      await onFailedAttempt(ctx)

      if (!(await shouldRetry(ctx))) throw normalizedError
      if (retriesLeft <= 0) throw normalizedError

      let delayMs = calculateDelay(retriesConsumed, {
        factor,
        minTimeout,
        maxTimeout,
        randomize,
      })
      if (computeDelay) {
        delayMs = computeDelay({ ...ctx, computedDelayMs: delayMs })
      }
      signal?.throwIfAborted()
      await sleep(delayMs, signal)
      signal?.throwIfAborted()

      retriesConsumed++
    }
  }

  throw new Error('Retry attempts exhausted without throwing an error.')
}
